import React, { useEffect, useRef, useState } from 'react';
import { Container, Card, Button, Row, Col, ListGroup, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';

function DesignDocumentReview() {
  const { token, refreshAccessToken } = useToken();
  const navigate = useNavigate();

  // keep redirect consistent with the new key
  useProgressRedirect('DOCUMENTATION_INTRO');

  const [selectedDFD, setSelectedDFD] = useState(null);
  const [threatModelProvided, setThreatModelProvided] = useState(false);

  const dfdElements = [
    { id: 'user', label: 'User', threats: ['Spoofing (S)', 'Tampering (T)'] },
    { id: 'web', label: 'Web Server', threats: ['Tampering (T)', 'Information Disclosure (I)'] },
    { id: 'db', label: 'Database', threats: ['Tampering (T)', 'Denial of Service (D)'] },
  ];

  const checkThreatModel = async () => {
    try {
      const authToken = token || await refreshAccessToken();
      const resp = await fetch('/api' + '/accounts/threat-model', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await resp.json();
      return data.threat_model_provided ?? false;
    } catch (err) {
      console.error('Failed to check threat model', err);
    }
  };

  const handleContinue = async () => {
    try {
      const authToken = token || await refreshAccessToken();

      const progressRes = await fetch('/api' + '/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: 'PLATFORM_TUTORIAL' }),
      });

      if (progressRes.ok) {
        navigate('/platform-tutorial', { replace: true });
      }
    } catch (err) {
      console.error('Failed to update progress', err);
    }
  };
  // No timer required for this intro step

  useEffect(() => {
    (async () => {
      const hasThreatModel = await checkThreatModel();
      setThreatModelProvided(hasThreatModel);
    })();
  }, []);


  return (
    <Container className="py-5">
      <ProgressTracker currentStep="documentation_intro" />

      <Card className="shadow-lg border-0 mb-4">
        <Card.Body>
          <h2 className="mb-3">📚 Documentation Concepts Intro</h2>
          <p>
            This step gives you time to review the concepts of documentation and design materials for your assigned target before beginning the pentest.
            Take a moment to read the provided documents and diagrams.
          </p>
          <p className="text-muted">Please read the provided documents and diagrams. When you're ready, click Proceed to continue.</p>

          <h5 className="mt-4">Software Design Documentation</h5>

          {/* Use-Case Diagram */}
          <Row className="mb-4">
            <Col md={12}>
              <h6 className="mb-2">Use-Case Diagram</h6>
              <p>
                A use-case diagram is a high-level representation used in software design to show how external actors interact with a system. 
                It focuses on functional requirements—what the system should do from the user's perspective.
              </p>
              <div className="border p-3 bg-light">
                <img 
                  src="/images/usecase_example.jpg" 
                  alt="Use-case diagram example" 
                  style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                />
              </div>
            </Col>
          </Row>

          {/* C4 Context Diagram */}
          <Row className="mb-4">
            <Col md={12}>
              <h6 className="mb-2">C4 Context Diagram</h6>
              <p>
                The C4 Context diagram provides a high-level view of the system and its relationships with external users and systems. 
                It shows who uses the system and what other systems it interacts with, focusing on the big picture without technical details.
              </p>
              <div className="border p-3 bg-light">
                <img 
                  src="/images/c4_context.jpg" 
                  alt="C4 Context diagram example" 
                  style={{ width: '100%', maxHeight: 500, objectFit: 'contain' }} 
                />
              </div>
            </Col>
          </Row>

          {/* C4 Container Diagram */}
          <Row className="mb-4">
            <Col md={12}>
              <h6 className="mb-2">C4 Container Diagram</h6>
              <p>
                The C4 Container diagram zooms into the system to show the high-level technical building blocks (containers) such as web applications, 
                databases, mobile apps, and microservices. It illustrates how these containers communicate and what technologies are used.
              </p>
              <div className="border p-3 bg-light">
                <img 
                  src="/images/c4_container.jpg" 
                  alt="C4 Container diagram example" 
                  style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                />
              </div>
            </Col>
          </Row>

          {/* C4 Component Diagram */}
          <Row className="mb-4">
            <Col md={12}>
              <h6 className="mb-2">C4 Component Diagram</h6>
              <p>
                The C4 Component diagram zooms into an individual container to show its internal structure. 
                It maps out the key components (classes, modules, or services) within the container and how they interact with each other.
              </p>
              <div className="border p-3 bg-light">
                <img 
                  src="/images/c4_component.jpg" 
                  alt="C4 Component diagram example" 
                  style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                />
              </div>
            </Col>
          </Row>
          {threatModelProvided && (<>
            <h5 className="mt-4">Security Threat Models</h5>

            {/* Data Flow Diagram */}
            <Row className="mb-4">
              <Col md={12}>
                <h6 className="mb-2">Data Flow Diagram (DFD)</h6>
                <p>
                  A Data Flow Diagram (DFD) shows how data moves through a system by illustrating processes, data stores, 
                  data flows, and external entities. It helps visualize the system's data architecture and identify potential 
                  security boundaries and trust zones.
                </p>
                <div className="border p-3 bg-light">
                  <img 
                    src="/images/dfd_example.jpg" 
                    alt="Data Flow Diagram example" 
                    style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                  />
                </div>
              </Col>
            </Row>

            {/* STRIDE Threat Model */}
            <Row className="mb-4">
              <Col md={12}>
                <h6 className="mb-2">STRIDE Threat Model</h6>
                <p>
                  STRIDE is a threat modeling framework developed by Microsoft that helps identify security threats by categorizing them into six types:
                </p>
                <ul className="mb-3">
                  <li><strong>S</strong>poofing - Pretending to be someone or something else</li>
                  <li><strong>T</strong>ampering - Modifying data or code</li>
                  <li><strong>R</strong>epudiation - Denying having performed an action</li>
                  <li><strong>I</strong>nformation Disclosure - Exposing information to unauthorized parties</li>
                  <li><strong>D</strong>enial of Service - Degrading or denying service to users</li>
                  <li><strong>E</strong>levation of Privilege - Gaining unauthorized access or permissions</li>
                </ul>
                <p>
                  <strong>STRIDE-per-Element:</strong> This approach applies STRIDE analysis to each element in a Data Flow Diagram. 
                  Different DFD elements (external entities, processes, data stores, and data flows) are susceptible to different types of threats. 
                  By systematically analyzing each element, security professionals can identify specific vulnerabilities and design appropriate countermeasures.
                </p>
                <p className="mb-2"><strong>Example Threat Analysis:</strong></p>
                <div className="border p-3 bg-light">
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Threat ID</th>
                        <th>Threat Statement</th>
                        <th>Element</th>
                        <th>STRIDE</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>T1</td>
                        <td>An attacker impersonates a legitimate customer to access booking functions.</td>
                        <td>Customer (EE)</td>
                        <td><strong>S – Spoofing</strong></td>
                      </tr>
                      <tr>
                        <td>T2</td>
                        <td>An attacker alters movie information flowing between the Movie Service and the database.</td>
                        <td>Data Flow</td>
                        <td><strong>T – Tampering</strong></td>
                      </tr>
                      <tr>
                        <td>T3</td>
                        <td>A customer denies making a booking request because the system lacks robust logging or audit trails.</td>
                        <td>Movie Service (Process)</td>
                        <td><strong>R – Repudiation</strong></td>
                      </tr>
                      <tr>
                        <td>T4</td>
                        <td>Sensitive movie schedule or booking data stored in the database is exposed due to insufficient access controls.</td>
                        <td>Movie Database (Data Store)</td>
                        <td><strong>I – Information Disclosure</strong></td>
                      </tr>
                      <tr>
                        <td>T5</td>
                        <td>An attacker floods the Movie Service with excessive requests, preventing legitimate access.</td>
                        <td>Movie Service (Process)</td>
                        <td><strong>D – Denial of Service</strong></td>
                      </tr>
                      <tr>
                        <td>T6</td>
                        <td>A customer exploits a flaw in the Movie Service to gain admin-level actions, such as modifying schedules.</td>
                        <td>Movie Service (Process)</td>
                        <td><strong>E – Elevation of Privilege</strong></td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Col>
            </Row>
          </>)}

          <div className="d-grid mt-4">
            <Button variant="success" size="lg" onClick={handleContinue}>Proceed to Platform Tutorial</Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default DesignDocumentReview;
